/**
 * Durable operation model for Espectro enter/exit flows.
 *
 * Replaces the ad-hoc pending-op localStorage records with a structured,
 * versioned schema that tracks the full lifecycle of each operation with
 * verified evidence at every stage.
 */

const STORAGE_OP_KEY = "espectro:operation";
const OLD_STORAGE_OP_KEY = "espectro:pending-op";
const SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Stage types
// ---------------------------------------------------------------------------

export type EnterStage =
  | "swap_created"
  | "source_broadcasted"
  | "inbound_detected"
  | "shielding_built"
  | "shielding_broadcasted"
  | "shield_confirmed"
  | "completed"
  | "failed"
  | "abandoned";

export type ExitStage =
  | "scan_prepared"
  | "exit_quote_ready"
  | "unshield_built"
  | "unshield_broadcasted"
  | "swap_pending"
  | "swap_completed"
  | "completed"
  | "failed"
  | "abandoned";

export type TransferStage =
  | "initiating"
  | "swap_quoting"
  | "swap_signing"
  | "swap_confirming"
  | "deposit_arriving"
  | "shielding"
  | "shield_confirming"
  | "exit_preparing"
  | "unshielding"
  | "exit_confirming"
  | "completed"
  | "failed"
  | "abandoned";

export type OperationStage = EnterStage | ExitStage | TransferStage;

const TERMINAL_STAGES: ReadonlySet<string> = new Set([
  "completed",
  "failed",
  "abandoned",
]);

export function isTerminalStage(stage: OperationStage): boolean {
  return TERMINAL_STAGES.has(stage);
}

// ---------------------------------------------------------------------------
// Evidence interfaces
// ---------------------------------------------------------------------------

export interface UtxoRef {
  txid: string;
  vout: number;
  amount: string;
  script: string;
  height: number;
}

export interface EnterEvidence {
  sourceAsset?: string;
  sourceAddress?: string;
  requestedAmount?: string;
  swapId?: string;
  swapTxHash?: string;
  evmTx?: Record<string, unknown>;
  knownUtxosBefore?: Array<{ txid: string; vout: number }>;
  depositUtxos?: UtxoRef[];
  shieldRawTx?: string;
  shieldTxId?: string;
  shieldedAmountZats?: number;
  preBalanceZats?: number;
}

export interface ExitEvidence {
  destinationAsset?: string;
  destinationAddress?: string;
  requestedAmount?: string;
  requestedAmountZats?: number;
  netZats?: number;
  swapId?: string;
  unshieldTarget?: string;
  unshieldRawTx?: string;
  unshieldTxId?: string;
  swapStatus?: string;
  preBalanceZats?: number;
}

export interface TransferEvidence {
  sourceAsset?: string;
  sourceAddress?: string;
  requestedAmount?: string;
  destinationAsset?: string;
  destinationAddress?: string;

  enterSwapId?: string;
  enterSwapTxHash?: string;
  approvalTx?: Record<string, unknown>;
  evmTx?: Record<string, unknown>;
  knownUtxosBefore?: Array<{ txid: string; vout: number }>;
  depositUtxos?: UtxoRef[];
  shieldRawTx?: string;
  shieldTxId?: string;
  shieldedAmountZats?: number;

  exitSwapId?: string;
  unshieldTarget?: string;
  unshieldRawTx?: string;
  unshieldTxId?: string;
  exitSwapStatus?: string;
  netZats?: number;

  expectedReceiveAmount?: string;
  expectedReceiveAmountMin?: string;
  exitSlippageBps?: number;
  exitSellAmountZec?: string;
}

// ---------------------------------------------------------------------------
// Operation record
// ---------------------------------------------------------------------------

export interface EspectroOperation {
  schemaVersion: number;
  id: string;
  type: "enter" | "exit" | "transfer";
  createdAt: number;
  updatedAt: number;
  stage: OperationStage;
  evidence: EnterEvidence | ExitEvidence | TransferEvidence;
  error?: string;
  retryCount: number;
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function saveOperation(op: EspectroOperation): void {
  op.updatedAt = Date.now();
  localStorage.setItem(STORAGE_OP_KEY, JSON.stringify(op));
}

export function loadOperation(): EspectroOperation | null {
  const migrated = migrateOldPendingOp();
  if (migrated) return migrated;

  try {
    const raw = localStorage.getItem(STORAGE_OP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EspectroOperation;
    if (parsed.schemaVersion !== SCHEMA_VERSION) {
      console.warn(
        `[espectro] Operation schema v${parsed.schemaVersion} != v${SCHEMA_VERSION}, discarding`,
      );
      localStorage.removeItem(STORAGE_OP_KEY);
      return null;
    }
    if (!parsed.id || !parsed.type || !parsed.stage) {
      console.warn("[espectro] Corrupted operation record, discarding");
      localStorage.removeItem(STORAGE_OP_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_OP_KEY);
    return null;
  }
}

export function clearOperation(): void {
  localStorage.removeItem(STORAGE_OP_KEY);
  localStorage.removeItem(OLD_STORAGE_OP_KEY);
}

// ---------------------------------------------------------------------------
// Migration from old pending-op format
// ---------------------------------------------------------------------------

function migrateOldPendingOp(): EspectroOperation | null {
  try {
    const raw = localStorage.getItem(OLD_STORAGE_OP_KEY);
    if (!raw) return null;

    const existing = localStorage.getItem(STORAGE_OP_KEY);
    if (existing) {
      localStorage.removeItem(OLD_STORAGE_OP_KEY);
      return null;
    }

    const old = JSON.parse(raw) as {
      type: string;
      data: Record<string, unknown>;
    };
    localStorage.removeItem(OLD_STORAGE_OP_KEY);

    const type = old.type as "enter" | "exit" | "transfer";
    const step = old.data.step as string | undefined;

    const op: EspectroOperation = {
      schemaVersion: SCHEMA_VERSION,
      id: generateId(),
      type,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      stage: mapOldStepToStage(type, step),
      evidence:
        type === "enter"
          ? ({
              swapId: old.data.swapId as string | undefined,
              swapTxHash: old.data.swapTxHash as string | undefined,
              shieldRawTx: old.data.raw_tx as string | undefined,
              shieldTxId: old.data.txid as string | undefined,
              evmTx: old.data.evmTx as Record<string, unknown> | undefined,
            } as EnterEvidence)
          : ({
              swapId: old.data.swapId as string | undefined,
              unshieldRawTx: old.data.raw_tx as string | undefined,
              unshieldTxId: old.data.txid as string | undefined,
            } as ExitEvidence),
      retryCount: 0,
    };

    saveOperation(op);
    console.log(
      `[espectro] Migrated old pending-op (${type}/${step}) -> operation (${op.stage})`,
    );
    return op;
  } catch {
    localStorage.removeItem(OLD_STORAGE_OP_KEY);
    return null;
  }
}

function mapOldStepToStage(
  type: string,
  step: string | undefined,
): OperationStage {
  if (type === "enter") {
    switch (step) {
      case "signing":
        return "swap_created";
      case "swapping":
        return "source_broadcasted";
      case "waiting_zec":
        return "source_broadcasted";
      case "shielding":
        return "inbound_detected";
      case "broadcasting":
        return "shielding_built";
      default:
        return "swap_created";
    }
  }
  switch (step) {
    case "broadcasting":
      return "unshield_built";
    case "swapping":
      return "unshield_broadcasted";
    default:
      return "scan_prepared";
  }
}

// ---------------------------------------------------------------------------
// Factory functions
// ---------------------------------------------------------------------------

export function createEnterOperation(params: {
  sourceAsset: string;
  sourceAddress: string;
  amount: string;
  preBalanceZats: number;
}): EspectroOperation {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: generateId(),
    type: "enter",
    createdAt: Date.now(),
    updatedAt: Date.now(),
    stage: "swap_created",
    evidence: {
      sourceAsset: params.sourceAsset,
      sourceAddress: params.sourceAddress,
      requestedAmount: params.amount,
      preBalanceZats: params.preBalanceZats,
    } as EnterEvidence,
    retryCount: 0,
  };
}

export function createExitOperation(params: {
  destinationAsset: string;
  destinationAddress: string;
  amount: string;
  preBalanceZats: number;
}): EspectroOperation {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: generateId(),
    type: "exit",
    createdAt: Date.now(),
    updatedAt: Date.now(),
    stage: "scan_prepared",
    evidence: {
      destinationAsset: params.destinationAsset,
      destinationAddress: params.destinationAddress,
      requestedAmount: params.amount,
      preBalanceZats: params.preBalanceZats,
    } as ExitEvidence,
    retryCount: 0,
  };
}

export function createTransferOperation(params: {
  sourceAsset: string;
  sourceAddress: string;
  amount: string;
  destinationAsset: string;
  destinationAddress: string;
}): EspectroOperation {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: generateId(),
    type: "transfer",
    createdAt: Date.now(),
    updatedAt: Date.now(),
    stage: "initiating",
    evidence: {
      sourceAsset: params.sourceAsset,
      sourceAddress: params.sourceAddress,
      requestedAmount: params.amount,
      destinationAsset: params.destinationAsset,
      destinationAddress: params.destinationAddress,
    } as TransferEvidence,
    retryCount: 0,
  };
}

// ---------------------------------------------------------------------------
// Transfer stage ordering (for resume logic)
// ---------------------------------------------------------------------------

const TRANSFER_STAGE_ORDER: readonly TransferStage[] = [
  "initiating",
  "swap_quoting",
  "swap_signing",
  "swap_confirming",
  "deposit_arriving",
  "shielding",
  "shield_confirming",
  "exit_preparing",
  "unshielding",
  "exit_confirming",
  "completed",
  "failed",
  "abandoned",
];

export function transferStageOrd(stage: TransferStage): number {
  return TRANSFER_STAGE_ORDER.indexOf(stage);
}

// ---------------------------------------------------------------------------
// Stage advancement
// ---------------------------------------------------------------------------

export function advanceOperation(
  op: EspectroOperation,
  nextStage: OperationStage,
  evidence?: Partial<EnterEvidence> | Partial<ExitEvidence>,
): EspectroOperation {
  if (isTerminalStage(op.stage)) {
    throw new Error(`Cannot advance terminal operation (stage=${op.stage})`);
  }
  op.stage = nextStage;
  if (evidence) {
    op.evidence = { ...op.evidence, ...evidence };
  }
  saveOperation(op);
  return op;
}

export function failOperation(
  op: EspectroOperation,
  error: string,
): EspectroOperation {
  op.stage = "failed";
  op.error = error;
  saveOperation(op);
  return op;
}

export function abandonOperation(op: EspectroOperation): EspectroOperation {
  op.stage = "abandoned";
  saveOperation(op);
  return op;
}

export function completeOperation(op: EspectroOperation): EspectroOperation {
  op.stage = "completed";
  saveOperation(op);
  return op;
}

// ---------------------------------------------------------------------------
// UI labels
// ---------------------------------------------------------------------------

export const STAGE_LABELS: Record<string, string> = {
  swap_created: "Swap initiated...",
  source_broadcasted: "Source transaction sent...",
  inbound_detected: "ZEC deposit detected...",
  shielding_built: "Shielding transaction built...",
  shielding_broadcasted: "Shielding broadcasted, waiting for confirmation...",
  shield_confirmed: "Shield confirmed!",

  scan_prepared: "Scanning shielded notes...",
  exit_quote_ready: "Exit quote ready...",
  unshield_built: "Unshielding transaction built...",
  unshield_broadcasted: "Unshielding broadcasted...",
  swap_pending: "Exit swap in progress...",
  swap_completed: "Exit swap completed!",

  completed: "Complete!",
  failed: "Failed",
  abandoned: "Abandoned",
};

export const TRANSFER_STAGE_LABELS: Record<TransferStage, string> = {
  initiating: "Preparing secure transfer...",
  swap_quoting: "Finding best route...",
  swap_signing: "Approve transaction in your wallet",
  swap_confirming: "Sending funds...",
  deposit_arriving: "Funds in transit...",
  shielding: "Breaking the trace...",
  shield_confirming: "Confirming privacy layer...",
  exit_preparing: "Preparing delivery...",
  unshielding: "Routing to destination...",
  exit_confirming: "Delivering funds...",
  completed: "Transfer complete",
  failed: "Transfer failed",
  abandoned: "Transfer abandoned",
};

// ---------------------------------------------------------------------------
// Resume action inference
// ---------------------------------------------------------------------------

export type ResumeAction =
  | "retry_broadcast_shield"
  | "retry_broadcast_unshield"
  | "wait_inbound"
  | "shield_stuck_funds"
  | "wait_shield_confirm"
  | "wait_swap"
  | "resume_exit_swap_tracking"
  | "none";

export type TransferResumeAction =
  | "resume_transfer"
  | "none";

export function getResumeAction(op: EspectroOperation): ResumeAction | TransferResumeAction {
  if (isTerminalStage(op.stage)) return "none";

  if (op.type === "transfer") {
    return "resume_transfer";
  }

  if (op.type === "enter") {
    const ev = op.evidence as EnterEvidence;
    switch (op.stage) {
      case "swap_created":
      case "source_broadcasted":
        return "wait_inbound";
      case "inbound_detected":
        return "shield_stuck_funds";
      case "shielding_built":
        return ev.shieldRawTx
          ? "retry_broadcast_shield"
          : "shield_stuck_funds";
      case "shielding_broadcasted":
        return "wait_shield_confirm";
      default:
        return "none";
    }
  }

  if (op.type === "exit") {
    const ev = op.evidence as ExitEvidence;
    switch (op.stage) {
      case "scan_prepared":
      case "exit_quote_ready":
        return "none";
      case "unshield_built":
        return ev.unshieldRawTx ? "retry_broadcast_unshield" : "none";
      case "unshield_broadcasted":
      case "swap_pending":
        return ev.swapId ? "resume_exit_swap_tracking" : "wait_swap";
      default:
        return "none";
    }
  }

  return "none";
}

/** True if the operation has chain commitments that make dismissal risky. */
export function hasChainCommitment(op: EspectroOperation): boolean {
  if (op.type === "enter") {
    const committed: ReadonlySet<string> = new Set([
      "inbound_detected",
      "shielding_built",
      "shielding_broadcasted",
      "shield_confirmed",
    ]);
    return committed.has(op.stage);
  }
  if (op.type === "exit") {
    const committed: ReadonlySet<string> = new Set([
      "unshield_built",
      "unshield_broadcasted",
      "swap_pending",
      "swap_completed",
    ]);
    return committed.has(op.stage);
  }
  if (op.type === "transfer") {
    const committed: ReadonlySet<string> = new Set([
      "swap_confirming",
      "deposit_arriving",
      "shielding",
      "shield_confirming",
      "exit_preparing",
      "unshielding",
      "exit_confirming",
    ]);
    return committed.has(op.stage);
  }
  return false;
}

/** True if the transfer operation has not yet sent funds on-chain (safe to wipe). */
export function isPreCommitTransferStage(stage: TransferStage): boolean {
  return stage === "initiating" || stage === "swap_quoting" || stage === "swap_signing";
}
